<script setup lang="ts">
import { onMounted, ref } from "vue";
import {
  BellRing,
  CheckCircle2,
  RefreshCw,
  Save,
  ShieldCheck,
  UserRound,
} from "lucide-vue-next";
import ExternalMfaSettings from "@/components/profile/ExternalMfaSettings.vue";
import AppShell from "@/components/workspace/AppShell.vue";
import { useWorkspaceStore } from "@/stores/workspace";

type DeliveryChoice = "immediate" | "daily" | "weekly" | "off";

const workspace = useWorkspaceStore();
const delivery = ref<DeliveryChoice>("daily");
const loading = ref(true);
const saving = ref(false);
const loadError = ref("");
const saveError = ref("");
const saved = ref(false);

async function loadPreferences() {
  loading.value = true;
  loadError.value = "";
  try {
    const current = await workspace.loadNotificationPreferences();
    delivery.value =
      current.digest !== "off"
        ? current.digest
        : current.immediate
          ? "immediate"
          : "off";
  } catch {
    loadError.value =
      "Your notification preferences could not be loaded. No settings were changed. Try again.";
  } finally {
    loading.value = false;
  }
}

async function savePreferences() {
  saving.value = true;
  saved.value = false;
  saveError.value = "";
  try {
    await workspace.saveNotificationPreferences({
      immediate: delivery.value === "immediate",
      digest:
        delivery.value === "daily" || delivery.value === "weekly"
          ? delivery.value
          : "off",
    });
    saved.value = true;
  } catch {
    saveError.value =
      "Your preferences could not be saved. Your selection is still here. Try again.";
  } finally {
    saving.value = false;
  }
}

onMounted(() => void loadPreferences());
</script>

<template>
  <AppShell>
    <div class="profile-page">
      <header class="profile-header">
        <div>
          <h1>Profile and preferences</h1>
          <p>Review your signed-in account and choose how updates reach you.</p>
        </div>
        <span v-if="saved" class="save-confirmation" role="status">
          <CheckCircle2 :size="16" /> Preferences saved
        </span>
      </header>

      <div class="profile-layout">
        <section class="profile-section sheet" aria-labelledby="account-title">
          <header>
            <span class="section-icon"><UserRound :size="20" /></span>
            <div>
              <h2 id="account-title">Account</h2>
              <p>Your account details come from your signed-in identity.</p>
            </div>
          </header>
          <dl class="account-details">
            <div>
              <dt>Name</dt>
              <dd>
                {{ workspace.user?.displayName || "Project participant" }}
              </dd>
            </div>
            <div>
              <dt>Email</dt>
              <dd>{{ workspace.user?.email || "Not available" }}</dd>
            </div>
            <div>
              <dt>Organization</dt>
              <dd>{{ workspace.user?.organization || "Not available" }}</dd>
            </div>
            <div>
              <dt>Access</dt>
              <dd>{{ workspace.user?.role || "Project participant" }}</dd>
            </div>
          </dl>
          <div class="account-note">
            <ShieldCheck :size="18" aria-hidden="true" />
            <p>
              Contact your workspace administrator if your name, email, or
              access is incorrect.
            </p>
          </div>
        </section>

        <ExternalMfaSettings
          v-if="workspace.user && !workspace.isInternal"
          class="security-section"
        />

        <section
          class="profile-section sheet"
          aria-labelledby="notifications-title"
        >
          <header>
            <span class="section-icon"><BellRing :size="20" /></span>
            <div>
              <h2 id="notifications-title">Notification preferences</h2>
              <p>Choose when routine project updates are delivered.</p>
            </div>
          </header>

          <div v-if="loading" class="preference-state" role="status">
            <RefreshCw class="spin" :size="18" />
            Loading preferences…
          </div>
          <div v-else-if="loadError" class="preference-error" role="alert">
            <p>{{ loadError }}</p>
            <button
              class="button button--secondary"
              type="button"
              @click="loadPreferences"
            >
              Try again
            </button>
          </div>
          <form v-else @submit.prevent="savePreferences">
            <fieldset :disabled="saving">
              <legend class="sr-only">Routine project updates</legend>
              <label>
                <input v-model="delivery" type="radio" value="immediate" />
                <span>
                  <strong>Immediately</strong>
                  <small>Send each routine update as it happens.</small>
                </span>
              </label>
              <label>
                <input v-model="delivery" type="radio" value="daily" />
                <span>
                  <strong>Daily digest</strong>
                  <small>One summary each morning.</small>
                </span>
              </label>
              <label>
                <input v-model="delivery" type="radio" value="weekly" />
                <span>
                  <strong>Weekly digest</strong>
                  <small>One summary on Monday morning.</small>
                </span>
              </label>
              <label>
                <input v-model="delivery" type="radio" value="off" />
                <span>
                  <strong>Off</strong>
                  <small>Do not send routine project updates.</small>
                </span>
              </label>
            </fieldset>
            <p class="preference-note">
              Mentions, invitations, access changes, and material changes to
              work you own are still sent immediately.
            </p>
            <p v-if="saveError" class="form-alert" role="alert">
              {{ saveError }}
            </p>
            <div class="profile-actions">
              <button
                class="button button--primary"
                type="submit"
                :disabled="saving"
              >
                <RefreshCw v-if="saving" class="spin" :size="16" />
                <Save v-else :size="16" />
                {{ saving ? "Saving…" : "Save preferences" }}
              </button>
            </div>
          </form>
        </section>
      </div>
    </div>
  </AppShell>
</template>

<style scoped>
.profile-page {
  width: min(1080px, 100%);
  margin: 0 auto;
  padding: 34px 32px 56px;
}
.profile-header {
  min-height: 68px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 22px;
}
.profile-header p,
.profile-section header p {
  margin-top: 5px;
  color: var(--text-soft);
}
.save-confirmation {
  min-height: 34px;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 6px 10px;
  border-radius: var(--radius-sm);
  color: var(--success);
  background: var(--success-soft);
  font-weight: 650;
}
.profile-layout {
  display: grid;
  grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr);
  gap: 20px;
  align-items: start;
}
.profile-section {
  overflow: hidden;
}
.security-section {
  grid-column: 1 / -1;
  grid-row: 2;
}
.profile-section > header {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 20px 22px;
  border-bottom: 1px solid var(--rule);
}
.section-icon {
  width: 36px;
  height: 36px;
  flex: 0 0 auto;
  display: grid;
  place-items: center;
  border-radius: 50%;
  color: var(--brand-strong);
  background: var(--brand-soft);
}
.account-details {
  margin: 0;
  padding: 4px 22px;
}
.account-details > div {
  display: grid;
  grid-template-columns: 112px minmax(0, 1fr);
  gap: 16px;
  padding: 14px 0;
  border-bottom: 1px solid var(--rule);
}
.account-details > div:last-child {
  border-bottom: 0;
}
.account-details dt {
  color: var(--text-soft);
  font-size: 12px;
  font-weight: 650;
}
.account-details dd {
  min-width: 0;
  margin: 0;
  overflow-wrap: anywhere;
  font-weight: 650;
}
.account-note {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 4px 16px 16px;
  padding: 12px;
  border-radius: var(--radius-sm);
  color: var(--text-soft);
  background: var(--sheet-strong);
}
.account-note svg {
  flex: 0 0 auto;
  color: var(--brand-strong);
}
.profile-section form {
  padding: 16px 22px 20px;
}
.profile-section fieldset {
  display: grid;
  gap: 4px;
  margin: 0;
  padding: 0;
  border: 0;
}
.profile-section fieldset label {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: flex-start;
  gap: 10px;
  padding: 10px 8px;
  border-radius: var(--radius-sm);
  cursor: pointer;
}
.profile-section fieldset label:hover {
  background: var(--sheet-subtle);
}
.profile-section input {
  margin-top: 3px;
  accent-color: var(--brand-strong);
}
.profile-section label span {
  display: grid;
  gap: 2px;
}
.profile-section label small {
  color: var(--text-soft);
}
.preference-note {
  margin-top: 14px;
  padding: 11px 12px;
  border-radius: var(--radius-sm);
  color: var(--text-soft);
  background: var(--sheet-strong);
  font-size: 12px;
}
.preference-state,
.preference-error {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 120px;
  padding: 22px;
  color: var(--text-soft);
}
.preference-error {
  align-items: flex-start;
  flex-direction: column;
  color: var(--danger);
}
.form-alert {
  margin-top: 14px;
  padding: 10px 12px;
  border-radius: var(--radius-sm);
  color: var(--danger);
  background: var(--danger-soft);
}
.profile-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 18px;
}
.spin {
  animation: spin 900ms linear infinite;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
@media (max-width: 900px) {
  .profile-page {
    padding-inline: 20px;
  }
  .profile-layout {
    grid-template-columns: 1fr;
  }
  .security-section {
    grid-column: auto;
    grid-row: auto;
  }
}
@media (max-width: 620px) {
  .profile-page {
    padding: 24px 14px 40px;
  }
  .profile-header {
    display: grid;
  }
  .account-details > div {
    grid-template-columns: 1fr;
    gap: 4px;
  }
}
</style>
